/**
 * The FILTER STATE of a screen (D528): the conditions of a request without the rest of it - `{ filters, anyOf, having }`. It is what a saved
 * filter set stores, what a global filter bar applies to every block of a panel, and what a shared link carries.
 *
 * Three rules live here so the three users of it cannot disagree:
 *  - it is validated by the SAME parser the planner uses (an unknown dimension, a wrong operator, a bad value or a cap is the planner's own
 *    reason code);
 *  - it holds NO PERSONAL VALUE: a condition on a customer, a driver, a courier or an order that carries a value (a key, an id, a text, a
 *    bound) is removed - {@link stripPersonalValues} - and the caller is told which dimensions lost one, so a screen can say so. A condition
 *    that carries no value (`is_null` / `not_null`) names nobody and stays;
 *  - it says which MARKETS and COUNTRIES it names, so a shared state can be shown only to an operator whose reach covers them all.
 *
 * Pure: no I/O.
 */
import { getDimension } from './catalog.js';
import { parseHaving } from './filter.js';
import { LIMITS, parseConditions } from './plan.js';
export const FILTER_STATE_LIMITS = { maxBytes: 8192, maxMarkets: 64, maxCountries: 64 };
/**
 * The size of a value as PostgreSQL stores it: `octet_length(state::text)` of a jsonb - BYTES of a text that has a space after every colon and
 * comma. The table's CHECK uses exactly this, so the API caps what the database would (a state that fits as compact JSON may not fit as jsonb text).
 * A structure deeper than 32 levels is `Infinity` (it is refused, never walked).
 */
export function jsonbTextBytes(value) {
    const text = (v, depth) => {
        if (depth > 32)
            throw new RangeError('too deep');
        if (v === null || v === undefined)
            return 'null';
        if (Array.isArray(v))
            return v.length === 0 ? '[]' : `[${v.map((x) => text(x, depth + 1)).join(', ')}]`;
        if (typeof v === 'object') {
            const entries = Object.entries(v);
            return entries.length === 0 ? '{}' : `{${entries.map(([k, x]) => `${JSON.stringify(k)}: ${text(x, depth + 1)}`).join(', ')}}`;
        }
        return JSON.stringify(v);
    };
    try {
        return new TextEncoder().encode(text(value, 0)).length;
    }
    catch {
        return Number.POSITIVE_INFINITY;
    }
}
const KEYS = new Set(['filters', 'anyOf', 'having']);
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const refuse = (code, field) => ({ ok: false, code, ...(field !== undefined ? { field } : {}) });
const VALUELESS = new Set(['is_null', 'not_null']);
/** Does this raw condition name a person or an order AND carry a value? (Unknown shapes are left for the parser to refuse.) */
function pinsAPerson(c) {
    if (!isObject(c) || typeof c.dimension !== 'string')
        return undefined;
    if (getDimension(c.dimension)?.personal !== true)
        return undefined;
    if (typeof c.op === 'string' && VALUELESS.has(c.op))
        return undefined;
    return c.dimension;
}
/**
 * Remove every condition that pins a person or an order. An OR group left with no condition is removed with them (an empty group could never
 * be satisfied). Anything that is not shaped like a state is returned untouched for the validator to refuse.
 */
export function stripPersonalValues(raw) {
    if (!isObject(raw))
        return { state: raw, stripped: [] };
    const stripped = [];
    const note = (d) => {
        if (!stripped.includes(d))
            stripped.push(d);
    };
    const keep = (list) => Array.isArray(list)
        ? list.filter((c) => {
            const d = pinsAPerson(c);
            if (d !== undefined)
                note(d);
            return d === undefined;
        })
        : list;
    const out = { ...raw };
    if ('filters' in raw)
        out.filters = keep(raw.filters);
    if (Array.isArray(raw.anyOf)) {
        // A group the strip EMPTIED goes with it; a group that was empty to begin with is left for the validator to refuse.
        out.anyOf = raw.anyOf
            .map((g) => ({ before: Array.isArray(g) ? g.length : -1, after: keep(g) }))
            .filter((g) => !(g.before > 0 && Array.isArray(g.after) && g.after.length === 0))
            .map((g) => g.after);
    }
    return { state: out, stripped };
}
/** Validate a filter state (after removing personal values). `having: false` refuses a state that has HAVING (a panel bar has none). */
export function checkFilterState(raw, options = {}) {
    if (!isObject(raw) || Object.keys(raw).some((k) => !KEYS.has(k)))
        return refuse('FILTER_STATE_INVALID', 'state');
    const size = jsonbTextBytes(raw);
    if (size === Number.POSITIVE_INFINITY)
        return refuse('FILTER_STATE_INVALID', 'state');
    if (size > FILTER_STATE_LIMITS.maxBytes)
        return refuse('FILTER_STATE_TOO_LARGE', 'state');
    const { state, stripped } = stripPersonalValues(raw);
    const s = state;
    const parsed = parseConditions(s.filters, s.anyOf);
    if (!parsed.ok)
        return parsed;
    const havingInput = s.having ?? [];
    if (!Array.isArray(havingInput) || havingInput.length > LIMITS.maxHaving)
        return refuse('HAVING_INVALID', 'having');
    if (options.having === false && havingInput.length > 0)
        return refuse('HAVING_NOT_ALLOWED_HERE', 'having');
    const havingMetrics = [];
    for (const h of havingInput) {
        const one = parseHaving(h, undefined);
        if (!one.ok)
            return one;
        if (!havingMetrics.includes(one.value.metric.id))
            havingMetrics.push(one.value.metric.id);
    }
    const all = [...parsed.value.top, ...parsed.value.groups.flat()];
    const markets = new Set();
    const countries = new Set();
    const regions = new Set();
    for (const c of all) {
        // every operator: "everything except beta" names beta as much as "only beta" does
        if (c.def.id === 'market')
            for (const v of c.values)
                markets.add(v);
        if (c.def.id === 'country')
            for (const v of c.values)
                countries.add(v);
        if (c.def.id === 'region')
            for (const v of c.values)
                regions.add(v);
    }
    const boundToMarket = parsed.value.top.some((c) => c.op === 'in' && (c.def.id === 'market' || c.def.id === 'country' || c.def.id === 'region'));
    const marketScopedValues = all.some((c) => (c.def.valueType === 'ref' && !c.def.personal && c.op !== 'is_null' && c.op !== 'not_null') || c.op === 'contains' || c.op === 'starts_with');
    // The checked state is the stripped input as written (the parser accepted it as it is, so a JSON round trip is a faithful deep copy).
    const copy = JSON.parse(JSON.stringify(s));
    // what is STORED (after the strip) is what the table's CHECK measures
    if (jsonbTextBytes(copy) > FILTER_STATE_LIMITS.maxBytes)
        return refuse('FILTER_STATE_TOO_LARGE', 'state');
    return {
        ok: true,
        value: { state: copy, stripped, personal: all.some((c) => c.def.personal), markets: [...markets], countries: [...countries], regions: [...regions], boundToMarket, marketScopedValues, havingMetrics },
    };
}
//# sourceMappingURL=filter-state.js.map